import { ref } from 'vue'
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  increment
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { TIME_SLOTS, TIME_SLOT_GROUPS, getSlotsByPeriod } from '@/utils/timeSlots'

/**
 * 꾸우챗 광고 관리 Composable
 *
 * 꾸우챗 결과에 맨 위에 추가로 노출되는 광고
 * 파트너가 캠페인 기반으로 신청 → Admin 승인 → 시간대별 노출
 *
 * 광고 상태:
 * - pending: 승인 대기
 * - approved: 승인됨 (노출중)
 * - rejected: 거절됨
 *
 * Firestore 컬렉션: chatAds
 */
export function useChatAd() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * 한국 시간 가져오기
   */
  const getKoreanTime = () => {
    const now = new Date()
    const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
    return koreaTime
  }

  /**
   * 현재 시간대에 활성화된 광고 목록 조회
   */
  const getActiveAds = async () => {
    loading.value = true
    error.value = null

    try {
      const now = getKoreanTime()
      const currentHour = now.getHours()
      const currentMinutes = now.getMinutes()
      const currentTime = `${String(currentHour).padStart(2, '0')}:${String(currentMinutes).padStart(2, '0')}`

      // approved 상태인 광고 조회
      const q = query(
        collection(db, 'chatAds'),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)

      // 현재 시간대에 해당하는 광고만 필터링
      const ads = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(ad => {
          if (!ad.timeSlot) return false
          return currentTime >= ad.timeSlot.start && currentTime < ad.timeSlot.end
        })

      return ads
    } catch (err) {
      console.error('꾸우챗 광고 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 랜덤 광고 1개 가져오기 (캐싱 포함)
   */
  let cachedAds = null
  let cacheTime = null
  const CACHE_DURATION = 5 * 60 * 1000 // 5분

  const getRandomAd = async () => {
    const now = Date.now()

    // 캐시가 유효하면 캐시에서 반환
    if (cachedAds && cacheTime && (now - cacheTime) < CACHE_DURATION) {
      if (cachedAds.length === 0) return null
      const randomIndex = Math.floor(Math.random() * cachedAds.length)
      return cachedAds[randomIndex]
    }

    // 캐시 갱신
    cachedAds = await getActiveAds()
    cacheTime = now

    if (cachedAds.length === 0) return null
    const randomIndex = Math.floor(Math.random() * cachedAds.length)
    return cachedAds[randomIndex]
  }

  /**
   * 클릭 수 증가
   */
  const incrementClick = async (adId) => {
    try {
      const adRef = doc(db, 'chatAds', adId)
      await updateDoc(adRef, {
        clicks: increment(1)
      })
    } catch (err) {
      console.error('클릭 수 증가 실패:', err)
    }
  }

  /**
   * 노출 수 증가
   */
  const incrementImpression = async (adId) => {
    try {
      const adRef = doc(db, 'chatAds', adId)
      await updateDoc(adRef, {
        impressions: increment(1)
      })
    } catch (err) {
      console.error('노출 수 증가 실패:', err)
    }
  }

  /**
   * 꾸우챗 광고 신청 (파트너)
   */
  const createChatAd = async (adData) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()

      const docRef = await addDoc(collection(db, 'chatAds'), {
        ...adData,
        status: 'pending',
        clicks: 0,
        impressions: 0,
        duration: '1week',
        rejectReason: '',
        createdAt: now,
        updatedAt: now
      })

      return docRef.id
    } catch (err) {
      console.error('꾸우챗 광고 신청 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 내 꾸우챗 광고 목록 조회 (파트너)
   */
  const getMyChatAds = async (partnerId) => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'chatAds'),
        where('partnerId', '==', partnerId),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('내 꾸우챗 광고 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 전체 꾸우챗 광고 목록 조회 (어드민)
   */
  const getAllChatAds = async (statusFilter = null) => {
    loading.value = true
    error.value = null

    try {
      let q
      if (statusFilter) {
        q = query(
          collection(db, 'chatAds'),
          where('status', '==', statusFilter),
          orderBy('createdAt', 'desc')
        )
      } else {
        q = query(
          collection(db, 'chatAds'),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('꾸우챗 광고 목록 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 꾸우챗 광고 상태 변경 (어드민)
   */
  const updateChatAdStatus = async (adId, status, rejectReason = '') => {
    loading.value = true
    error.value = null

    try {
      const adRef = doc(db, 'chatAds', adId)
      const now = new Date().toISOString()

      const updateData = {
        status,
        updatedAt: now
      }

      if (status === 'approved') {
        // 승인 시 1주일 후 만료
        const expiresAt = new Date()
        expiresAt.setDate(expiresAt.getDate() + 7)
        updateData.approvedAt = now
        updateData.expiresAt = expiresAt.toISOString()
      } else if (status === 'rejected') {
        updateData.rejectReason = rejectReason
        updateData.rejectedAt = now
      }

      await updateDoc(adRef, updateData)
      return true
    } catch (err) {
      console.error('꾸우챗 광고 상태 변경 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 꾸우챗 광고 취소/삭제 (파트너 - pending 상태만)
   */
  const cancelChatAd = async (adId) => {
    loading.value = true
    error.value = null

    try {
      await deleteDoc(doc(db, 'chatAds', adId))
      return true
    } catch (err) {
      console.error('꾸우챗 광고 취소 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 기존 꾸우챗 광고 확인 (pending 또는 approved 상태)
   */
  const checkExistingChatAd = async (partnerId) => {
    try {
      // pending 상태 확인
      const pendingQ = query(
        collection(db, 'chatAds'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'pending')
      )
      const pendingSnapshot = await getDocs(pendingQ)
      if (!pendingSnapshot.empty) return true

      // approved 상태 확인
      const approvedQ = query(
        collection(db, 'chatAds'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'approved')
      )
      const approvedSnapshot = await getDocs(approvedQ)
      return !approvedSnapshot.empty
    } catch (err) {
      console.error('꾸우챗 광고 확인 실패:', err)
      return false
    }
  }

  return {
    loading,
    error,
    TIME_SLOTS,
    TIME_SLOT_GROUPS,
    getSlotsByPeriod,
    getKoreanTime,
    getActiveAds,
    getRandomAd,
    incrementClick,
    incrementImpression,
    createChatAd,
    getMyChatAds,
    getAllChatAds,
    updateChatAdStatus,
    cancelChatAd,
    checkExistingChatAd
  }
}
